import { GraphQLError } from 'graphql';

/**
 * Centralized Error Handling Service
 * Requirements: 5.5, 7.5, 10.2 - Comprehensive error handling with proper status codes
 */

export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface AppError {
  type: ErrorType;
  message: string;
  code?: string;
  statusCode?: number;
  details?: any;
  timestamp: Date;
  retryable: boolean;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorListeners: ((error: AppError) => void)[] = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Add error listener for global error handling
   */
  addErrorListener(listener: (error: AppError) => void): void {
    this.errorListeners.push(listener);
  }

  /**
   * Remove error listener
   */
  removeErrorListener(listener: (error: AppError) => void): void {
    const index = this.errorListeners.indexOf(listener);
    if (index > -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  /**
   * Handle and classify errors
   */
  handleError(error: any): AppError {
    const appError = this.classifyError(error);
    
    // Log error
    this.logError(appError, error);
    
    // Notify listeners
    this.errorListeners.forEach(listener => {
      try {
        listener(appError);
      } catch (listenerError) {
        console.error('Error in error listener:', listenerError);
      }
    });

    return appError;
  }

  /**
   * Classify different types of errors
   */
  private classifyError(error: any): AppError {
    const timestamp = new Date();

    // Network errors
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      return {
        type: ErrorType.NETWORK_ERROR,
        message: 'Network connection failed. Please check your internet connection.',
        code: error.code,
        statusCode: 0,
        details: error,
        timestamp,
        retryable: true
      };
    }

    // GraphQL errors
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const graphQLError = error.graphQLErrors[0];
      return this.handleGraphQLError(graphQLError, timestamp);
    }

    // HTTP errors
    if (error.response) {
      return this.handleHttpError(error, timestamp);
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: ErrorType.TIMEOUT_ERROR,
        message: 'Request timed out. Please try again.',
        code: error.code,
        details: error,
        timestamp,
        retryable: true
      };
    }

    // Authentication errors
    if (error.name === 'NotAuthorizedException' || error.code === 'NotAuthorizedException') {
      return {
        type: ErrorType.AUTHENTICATION_ERROR,
        message: 'Authentication failed. Please sign in again.',
        code: error.code,
        statusCode: 401,
        details: error,
        timestamp,
        retryable: false
      };
    }

    // Authorization errors
    if (error.name === 'UnauthorizedError' || error.code === 'UnauthorizedError') {
      return {
        type: ErrorType.AUTHORIZATION_ERROR,
        message: 'You do not have permission to perform this action.',
        code: error.code,
        statusCode: 403,
        details: error,
        timestamp,
        retryable: false
      };
    }

    // Validation errors
    if (error.name === 'ValidationError' || error.code === 'ValidationError') {
      return {
        type: ErrorType.VALIDATION_ERROR,
        message: error.message || 'Invalid input provided.',
        code: error.code,
        statusCode: 400,
        details: error,
        timestamp,
        retryable: false
      };
    }

    // Default unknown error
    return {
      type: ErrorType.UNKNOWN_ERROR,
      message: error.message || 'An unexpected error occurred.',
      code: error.code,
      details: error,
      timestamp,
      retryable: false
    };
  }

  /**
   * Handle GraphQL specific errors
   */
  private handleGraphQLError(error: GraphQLError, timestamp: Date): AppError {
    const extensions = error.extensions;
    
    if (extensions?.code === 'UNAUTHENTICATED') {
      return {
        type: ErrorType.AUTHENTICATION_ERROR,
        message: 'Authentication required. Please sign in.',
        code: extensions.code,
        statusCode: 401,
        details: error,
        timestamp,
        retryable: false
      };
    }

    if (extensions?.code === 'FORBIDDEN') {
      return {
        type: ErrorType.AUTHORIZATION_ERROR,
        message: 'Access denied. You do not have permission for this action.',
        code: extensions.code,
        statusCode: 403,
        details: error,
        timestamp,
        retryable: false
      };
    }

    if (extensions?.code === 'BAD_USER_INPUT') {
      return {
        type: ErrorType.VALIDATION_ERROR,
        message: error.message || 'Invalid input provided.',
        code: extensions.code,
        statusCode: 400,
        details: error,
        timestamp,
        retryable: false
      };
    }

    return {
      type: ErrorType.SERVER_ERROR,
      message: error.message || 'Server error occurred.',
      code: extensions?.code,
      statusCode: 500,
      details: error,
      timestamp,
      retryable: true
    };
  }

  /**
   * Handle HTTP errors
   */
  private handleHttpError(error: any, timestamp: Date): AppError {
    const status = error.response?.status;
    const data = error.response?.data;

    switch (status) {
      case 400:
        return {
          type: ErrorType.VALIDATION_ERROR,
          message: data?.message || 'Invalid request.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: false
        };

      case 401:
        return {
          type: ErrorType.AUTHENTICATION_ERROR,
          message: 'Authentication required. Please sign in.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: false
        };

      case 403:
        return {
          type: ErrorType.AUTHORIZATION_ERROR,
          message: 'Access denied.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: false
        };

      case 404:
        return {
          type: ErrorType.NOT_FOUND_ERROR,
          message: 'Resource not found.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: false
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: ErrorType.SERVER_ERROR,
          message: 'Server error. Please try again later.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: true
        };

      default:
        return {
          type: ErrorType.UNKNOWN_ERROR,
          message: data?.message || error.message || 'An error occurred.',
          statusCode: status,
          details: error,
          timestamp,
          retryable: false
        };
    }
  }

  /**
   * Log errors appropriately
   */
  private logError(appError: AppError, originalError: any): void {
    const logData = {
      type: appError.type,
      message: appError.message,
      code: appError.code,
      statusCode: appError.statusCode,
      timestamp: appError.timestamp,
      retryable: appError.retryable,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // In development, log full error details
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${appError.type}`);
      console.error('App Error:', logData);
      console.error('Original Error:', originalError);
      console.groupEnd();
    } else {
      // In production, log minimal info and send to monitoring service
      console.error('Error:', appError.type, appError.message);
      
      // Example: Send to error monitoring service
      // this.sendToMonitoringService(logData, originalError);
    }
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: AppError): string {
    switch (error.type) {
      case ErrorType.NETWORK_ERROR:
        return 'Connection problem. Please check your internet and try again.';
      
      case ErrorType.AUTHENTICATION_ERROR:
        return 'Please sign in to continue.';
      
      case ErrorType.AUTHORIZATION_ERROR:
        return 'You don\'t have permission for this action.';
      
      case ErrorType.VALIDATION_ERROR:
        return error.message || 'Please check your input and try again.';
      
      case ErrorType.NOT_FOUND_ERROR:
        return 'The requested item could not be found.';
      
      case ErrorType.SERVER_ERROR:
        return 'Server is temporarily unavailable. Please try again later.';
      
      case ErrorType.TIMEOUT_ERROR:
        return 'Request timed out. Please try again.';
      
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryable(error: AppError): boolean {
    return error.retryable;
  }

  /**
   * Static helper to classify a raw error (e.g. a caught exception) and
   * determine whether it represents a retryable condition, without
   * requiring callers to first convert it into an AppError.
   */
  static isRetryableError(error: any): boolean {
    const instance = ErrorHandler.getInstance();
    const appError = instance.classifyError(error);
    return appError.retryable;
  }

  /**
   * Get retry delay based on error type
   */
  getRetryDelay(error: AppError, attempt: number): number {
    if (!error.retryable) return 0;

    // Exponential backoff with jitter
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    
    // Add jitter (±25%)
    const jitter = delay * 0.25 * (Math.random() - 0.5);
    return Math.max(delay + jitter, 100);
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();